<template>
  <div class="forum">
    <v-container>
      <div class="d-flex align-center">
        <h1 class="mb-5">Developers</h1>
        <v-text-field 
          label="Search (use ',' for several conditions)" 
          outlined 
          hide-details 
          v-model="search"
          class="ml-5 mr-5"
        ></v-text-field>
        <v-dialog
          v-model="new_dev_dialog"
          persistent
          max-width="600px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              depressed 
              color="primary"
              x-large
              v-bind="attrs"
              v-on="on"
            >
              add
              <v-icon right>mdi-plus</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">Add new developer</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-text-field
                  v-model="new_dev.name"
                  label="Name"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
                </v-container>
              </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click="onPressCloseNewDevDialog"
              >
                Close
              </v-btn>
              <v-btn
                color="blue darken-1"
                text
                @click="onPressAddNewDev"
              >
                Add
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
       <div class="d-flex justify-end align-center ml-5">
          <v-switch
            v-model="show_public_only"
            label="Show public only"
            class="mr-5"
          ></v-switch>
          <v-switch
            v-model="show_processed_only"
            label="Show reviewed only"
            class="mr-5"
          ></v-switch>
          <v-switch
            v-model="show_working_only"
            label="Show working only"
            class="mr-5"
          ></v-switch>
          <v-btn 
            depressed 
            color="primary"
            @click="getDevsExcelWithRates"
          >
            download devs table
            <v-icon right>mdi-arrow-down</v-icon>
          </v-btn>
        </div>
      <v-data-table
        :headers="headers"
        :items="filteredData"
        class="elevation-1"
        :loading="loading"
        :items-per-page="50"
      >
        <template v-slot:item.visibility="{ item }">
          <v-btn color="secondary" v-if="!item.show_to_clients" @click="setPublic(item, true)" icon>
            <v-icon>mdi-eye-off</v-icon>
          </v-btn>
          <v-btn v-else color="warning" @click="setPublic(item, false)" icon>
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </template>
        <template v-slot:item.is_busy_with_our_project="{ item }">
          <v-icon color="warning" v-if="item.is_busy_with_our_project">mdi-pickaxe</v-icon>
          <v-icon color="secondary" v-else>mdi-sleep</v-icon>
        </template>
        <template v-slot:item.user="{ item }">
          <v-icon color="warning" v-if="item.user">mdi-thumb-up</v-icon>
          <v-icon color="secondary" v-else>mdi-thumb-down-outline</v-icon>
        </template>
        <template v-slot:item.name="{ item }">
          {{ item.name }}
        </template>
        <template v-slot:item.rate="{ item }">
          {{ item.rate_parttime ? item.rate_parttime + '$' : '-' }}
          /
          {{ item.rate_fulltime ? item.rate_fulltime + '$' : '-' }}
        </template>
        <template v-slot:item.techs="{ item }">
          <v-card v-if="item.techs.filter(n => n.core).length" flat class="d-flex pa-2 ma-2 black flex-column align-start">
            <v-chip 
              small 
              color="accent" 
              class="black--text font-weight-bold mb-1" 
              v-for="n in item.techs.filter(n => n.core).map(n => n.name)"
            >
              {{ n }}
            </v-chip>
          </v-card>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ $formatDate(item.createdAt) }}
        </template>
        <template v-slot:item.cv="{ item }">
          <v-btn
            color="primary"
            depressed
            class="mr-4"
            :href="item.cv_url"
            :disabled="!item.cv_url"
            target="_blank"
            small
          >
            pdf
            <v-icon small right>mdi-file-document-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn color="error" icon @click="remove(item)">
            <v-icon
              color="error"
              small
            >
              mdi-delete
            </v-icon>
          </v-btn>
          <v-btn color="accent" icon @click="edit(item)">
            <v-icon
              color="accent"
              small
            >
              mdi-arrow-right
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-container>

    <SingleDev/>

  </div>
</template>

<script>
import { DevsAPI } from '@/api';
import SingleDev from '@/components/single-dev/SingleDev';
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      loading: false,
      search: '',
      show_processed_only: false,
      show_working_only: false,
      show_public_only: false,
      new_dev_dialog: false,
      new_dev: {},
      headers: [
        { text: 'Public', value: 'visibility' },
        { text: 'Busy?', value: 'is_busy_with_our_project' },
        { text: 'Has user?', value: 'user' },
        { text: 'ID', value: 'pub_id' },
        { text: 'Name', value: 'name' },
        { text: 'Rate part/full', value: 'rate' },
        { text: 'Main technologies', value: 'techs' },
        { text: 'Added', value: 'createdAt' },
        { text: 'CV', value: 'cv' },
        { text: 'Actions', value: 'actions' },
      ],
    }
  },
  components: {
    SingleDev,
  },
  methods: {
    sync() {
      this.loading = true;
      return this.$store.dispatch('devs/updateData')
        .finally(() => {
          this.loading = false;
        })
    },

    setPublic(item, flag) {
      this.loading = true;
      return DevsAPI.setVisibility(item._id, flag)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },

    remove(item) {
      this.loading = true;
      return DevsAPI.remove(item._id)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },
    
    edit(item) {
      this.$store.commit('devs/setForEdit', item)
      this.$store.commit('devs/openModal')
    },

    onPressCloseNewDevDialog() {
      this.new_dev_dialog = false;
      this.new_dev = {};
    },

    onPressAddNewDev() {
      this.loading = true;
      return DevsAPI.createDev(this.new_dev)
        .then(() => {
          this.sync();
          this.new_dev_dialog = false;
          this.new_dev = {};
        })
        .finally(() => {
          this.loading = false;
        })
    },

    getDevsExcelWithRates() {
      return DevsAPI.getDevsExcelWithRates().then((response) => {
        const url = URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `devs.xlsx`
        )
        document.body.appendChild(link)
        link.click()
      })
    },

  },

  created() {
    this.sync();
  },

  computed: {
    ...mapGetters({
      data: 'devs/data',
    }),

    filteredData() {

      const regex = this.search
        .split(',')
        .filter(n => n && n.length)
        .map(n => new RegExp(n.trim(), 'i'))

      return this.data.filter(n => {
        const found_tech = regex.every(z => n.techs.find(n => z.test(n.name)))
        const found_name = regex.every(z => z.test(n.name))
        return found_name || found_tech;
      }).filter(n => {
        const cv_ok = !this.show_processed_only || (n.cv_url && n.cv_url.length);
        const is_working = !this.show_working_only || n.is_busy_with_our_project;
        const is_public = !this.show_public_only || n.show_to_clients;
        return cv_ok && is_working && is_public;
      })
    }
  },
}
</script>

<style lang="scss" scoped>

.devs {
  
}

</style>